import { useEffect, useState } from "react"
import axios from "axios"
import { Box, Flex } from "@chakra-ui/react"

/**
 * PUBLIC page: esoportal.io/sms-opt-in — "Keep in touch with ESO Fund by text".
 *
 * No login. It is also the public opt-in URL for the toll-free verification (TFV) filing, so
 * a carrier reviewer must be able to open it and see exactly what a visitor sees.
 *
 * The consent wording is fetched from the server (GET /sms/opt-in/copy) and rendered
 * verbatim; the server stores ITS copy for that version as evidence, so this page must never
 * hard-code or edit the disclosure. The checkbox starts unticked and is not pre-filled.
 * US/Canada (+1) numbers only — the only numbers our texts can reach.
 */

interface Copy { version: string; label: string; body: string; senderNumber: string }

const API = process.env.REACT_APP_SERVER_URL

const input: React.CSSProperties = {
  width: "100%", padding: "10px 12px", fontSize: 16, border: "1px solid #cbd5e0",
  borderRadius: 6, boxSizing: "border-box",
}
const label: React.CSSProperties = { display: "block", fontSize: 14, fontWeight: 600, margin: "16px 0 6px", color: "#2d3748" }

export const SmsOptInPage = () => {
  const [copy, setCopy] = useState<Copy | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [name, setName] = useState("")
  const [company, setCompany] = useState("")
  const [phone, setPhone] = useState("")
  const [website, setWebsite] = useState("") // honeypot: hidden from people
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    axios.get(`${API}/sms/opt-in/copy`)
      .then((r) => setCopy(r.data?.data ?? null))
      .catch(() => setLoadError(true))
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!copy) return
    setError(null)
    if (!consent) { setError("Please tick the box to agree to receive texts."); return }
    setSubmitting(true)
    try {
      await axios.post(`${API}/sms/opt-in`, { name, company, phone, website, consent: true, version: copy.version })
      setDone(true)
    } catch (err: any) {
      setError(err?.response?.data?.error || "Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Flex minH="100vh" bg="#f4f6f8" justify="center" align="flex-start" py={{ base: 6, md: 16 }} px={4}>
      <Box bg="white" borderRadius="8px" boxShadow="sm" p={{ base: 6, md: 10 }} w="100%" maxW="560px">
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px", color: "#1f4e79" }}>
          Keep in touch with ESO Fund by text
        </h1>
        <p style={{ margin: "0 0 8px", color: "#4a5568", fontSize: 15, lineHeight: 1.5 }}>
          Get text updates from ESO Fund about your inquiry or transaction. US and Canadian mobile numbers only.
        </p>

        {done ? (
          <div role="status" style={{ marginTop: 24, padding: 16, background: "#f0fff4", border: "1px solid #9ae6b4", borderRadius: 6, color: "#22543d" }}>
            Thanks, you're signed up. You can reply STOP to any of our texts at any time to opt out.
          </div>
        ) : loadError ? (
          <p style={{ marginTop: 24, color: "#c53030" }}>
            This page couldn't load. Please refresh, or email support@esofund.com.
          </p>
        ) : (
          <form onSubmit={submit} noValidate>
            <label style={label} htmlFor="sms-name">Name</label>
            <input id="sms-name" style={input} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required maxLength={120} />

            <label style={label} htmlFor="sms-company">Company <span style={{ fontWeight: 400, color: "#718096" }}>(optional)</span></label>
            <input id="sms-company" style={input} value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" maxLength={160} />

            <label style={label} htmlFor="sms-phone">Mobile number</label>
            <input id="sms-phone" style={input} value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 555-5555" required maxLength={40} />

            {/* Honeypot: off-screen and out of the tab order, so people never fill it. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
              <label htmlFor="sms-website">Website</label>
              <input id="sms-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 22, cursor: "pointer" }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={!copy} style={{ marginTop: 4, width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5, color: "#2d3748" }}>
                {copy ? (<><strong>{copy.label}</strong> {copy.body}</>) : "Loading…"}
              </span>
            </label>
            <p style={{ fontSize: 13, margin: "8px 0 0 28px" }}>
              <a href="/sms-terms" style={{ color: "#2b6cb0" }}>SMS Terms</a>
              {" · "}
              <a href="https://www.esofund.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#2b6cb0" }}>Privacy Policy</a>
            </p>

            {error && <p role="alert" style={{ color: "#c53030", marginTop: 16 }}>{error}</p>}

            <button type="submit" disabled={submitting || !copy}
              style={{ marginTop: 24, width: "100%", padding: "12px", fontSize: 16, fontWeight: 600, color: "white", background: submitting || !copy ? "#a0aec0" : "#1f4e79", border: 0, borderRadius: 6, cursor: submitting || !copy ? "default" : "pointer" }}>
              {submitting ? "Signing up…" : "Sign up for texts"}
            </button>
          </form>
        )}
      </Box>
    </Flex>
  )
}
